import sanitizeHtml from 'sanitize-html';

export function getPaginationItems(
	page: number,
	totalPages: number
): (number | 'ellipsis')[] {
	const items: (number | 'ellipsis')[] = [];

	if (totalPages <= 7) {
		for (let i = 1; i <= totalPages; i++) items.push(i);
	} else {
		if (page <= 3) {
			items.push(1, 2, 3, 4, 5, 'ellipsis', totalPages);
		} else if (page >= totalPages - 2) {
			items.push(1, 'ellipsis');
			for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
		} else {
			items.push(
				1,
				'ellipsis',
				page - 1,
				page,
				page + 1,
				'ellipsis',
				totalPages
			);
		}
	}

	return items;
}

export function shortenByWords(text: string, wordLimit: number) {
	const words = text.trim().split(' ');
	if (words.length <= wordLimit) return text;
	return words.slice(0, wordLimit).join(' ') + '...';
}

export function shortenByChars(text: string, charLimit: number) {
	const trimmed = text.trim();
	if (trimmed.length <= charLimit) return trimmed;

	let result = trimmed.slice(0, charLimit);

	// if cut within word
	const lastSpace = result.lastIndexOf(' ');
	if (lastSpace > 0) {
		result = result.slice(0, lastSpace);
	}

	return result + '...';
}

export function getInitials(firstname: string) {
	return firstname.charAt(0).toUpperCase() + firstname.charAt(1).toUpperCase();
}

export function getFileType(
	url: string
): 'video' | 'image' | 'pdf' | 'unknown' {
	const lowerUrl = url.toLowerCase();

	const videoExtensions = ['.mp4', '.webm', '.mkv', '.avi', '.mov'];
	const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
	const pdfExtensions = ['.pdf'];

	if (videoExtensions.some((ext) => lowerUrl.endsWith(ext))) {
		return 'video';
	}
	if (imageExtensions.some((ext) => lowerUrl.endsWith(ext))) {
		return 'image';
	}
	if (pdfExtensions.some((ext) => lowerUrl.endsWith(ext))) {
		return 'pdf';
	}

	return 'unknown';
}

export function mapKnownCountriesToIso(country: string) {
	const splitCountry = country.split('(')[0].trim();
	switch (splitCountry) {
		case 'Deutschland':
			return 'de';
		case 'Schweiz':
			return 'ch';
		case 'Österreich':
			return 'at';
		case 'Afghanistan':
			return 'af';
		case 'Åland Islands':
			return 'ax';
		case 'Albania':
			return 'al';
		case 'Algeria':
			return 'dz';
		case 'American Samoa':
			return 'as';
		case 'Andorra':
			return 'ad';
		case 'Angola':
			return 'ao';
		case 'Anguilla':
			return 'ai';
		case 'Antarctica':
			return 'aq';
		case 'Antigua and Barbuda':
			return 'ag';
		case 'Argentina':
			return 'ar';
		case 'Armenia':
			return 'am';
		case 'Aruba':
			return 'aw';
		case 'Australia':
			return 'au';
		case 'Azerbaijan':
			return 'az';
		case 'Bahamas':
			return 'bs';
		case 'Bahrain':
			return 'bh';
		case 'Bangladesh':
			return 'bd';
		case 'Barbados':
			return 'bb';
		case 'Belarus':
			return 'by';
		case 'Belgium':
			return 'be';
		case 'Belize':
			return 'bz';
		case 'Benin':
			return 'bj';
		case 'Bermuda':
			return 'bm';
		case 'Bhutan':
			return 'bt';
		case 'Bolivia':
			return 'bo';
		case 'Bonaire Saint Eustatius and Saba':
			return 'bq';
		case 'Bosnia and Herzegovina':
			return 'ba';
		case 'Botswana':
			return 'bw';
		case 'Bouvet Island':
			return 'bv';
		case 'Brazil':
			return 'br';
		case 'British Indian Ocean Territory':
			return 'io';
		case 'British Virgin Islands':
			return 'vg';
		case 'Brunei':
			return 'bn';
		case 'Bulgaria':
			return 'bg';
		case 'Burkina Faso':
			return 'bf';
		case 'Burma':
			return 'mm';
		case 'Burundi':
			return 'bi';
		case 'Cambodia':
			return 'kh';
		case 'Cameroon':
			return 'cm';
		case 'Canada':
			return 'ca';
		case 'Cape Verde':
			return 'cv';
		case 'Cayman Islands':
			return 'ky';
		case 'Central African Republic':
			return 'cf';
		case 'Chad':
			return 'td';
		case 'Chile':
			return 'cl';
		case 'China':
			return 'cn';
		case 'Christmas Island':
			return 'cx';
		case 'Colombia':
			return 'co';
		case 'Comoros':
			return 'km';
		case 'Cook Islands':
			return 'ck';
		case 'Corsica':
			return 'fr';
		case 'Costa Rica':
			return 'cr';
		case 'Croatia':
			return 'hr';
		case 'Cuba':
			return 'cu';
		case 'Cyprus':
			return 'cy';
		case 'Czech Republic':
			return 'cz';
		case 'Denmark':
			return 'dk';
		case 'Djibouti':
			return 'dj';
		case 'Dominica':
			return 'dm';
		case 'Dominican Republic':
			return 'do';
		case 'Ecuador':
			return 'ec';
		case 'Egypt':
			return 'eg';
		case 'El Salvador':
			return 'sv';
		case 'Equatorial Guinea':
			return 'gq';
		case 'Eritrea':
			return 'er';
		case 'Estonia':
			return 'ee';
		case 'Ethiopia':
			return 'et';
		case 'Faroe Islands':
			return 'fo';
		case 'Federated States of Micronesia':
			return 'fm';
		case 'Fiji':
			return 'fj';
		case 'Finland':
			return 'fi';
		case 'France':
			return 'fr';
		case 'French Guiana':
			return 'gf';
		case 'French Polynesia':
			return 'pf';
		case 'Gabon':
			return 'ga';
		case 'Gambia':
			return 'gm';
		case 'Georgia':
			return 'ge';
		case 'Ghana':
			return 'gh';
		case 'Gibraltar':
			return 'gi';
		case 'Greece':
			return 'gr';
		case 'Greenland':
			return 'gl';
		case 'Grenada':
			return 'gd';
		case 'Guadeloupe':
			return 'gp';
		case 'Guam':
			return 'gu';
		case 'Guatemala':
			return 'gt';
		case 'Guernsey':
			return 'gg';
		case 'Guinea':
			return 'gn';
		case 'Guinea-Bissau':
			return 'gw';
		case 'Guyana':
			return 'gy';
		case 'Haiti':
			return 'ht';
		case 'Heard Island and McDonald Islands':
			return 'hm';
		case 'Honduras':
			return 'hn';
		case 'Hong Kong':
			return 'hk';
		case 'Hungary':
			return 'hu';
		case 'Iceland':
			return 'is';
		case 'India':
			return 'in';
		case 'Indonesia':
			return 'id';
		case 'Iraq':
			return 'iq';
		case 'Ireland':
			return 'ie';
		case 'Isle of Man':
			return 'im';
		case 'Israel':
			return 'il';
		case 'Italy':
			return 'it';
		case 'Jamaica':
			return 'jm';
		case 'Japan':
			return 'jp';
		case 'Jersey':
			return 'je';
		case 'Jordan':
			return 'jo';
		case 'Kazakhstan':
			return 'kz';
		case 'Kenya':
			return 'ke';
		case 'Kiribati':
			return 'ki';
		case 'Kuwait':
			return 'kw';
		case 'Kyrgyzstan':
			return 'kg';
		case 'Latvia':
			return 'lv';
		case 'Lebanon':
			return 'lb';
		case 'Lesotho':
			return 'ls';
		case 'Liberia':
			return 'lr';
		case 'Libya':
			return 'ly';
		case 'Liechtenstein':
			return 'li';
		case 'Lithuania':
			return 'lt';
		case 'Luxembourg':
			return 'lu';
		case 'Macao':
			return 'mo';
		case 'Madagascar':
			return 'mg';
		case 'Malawi':
			return 'mw';
		case 'Malaysia':
			return 'my';
		case 'Maldives':
			return 'mv';
		case 'Mali':
			return 'ml';
		case 'Malta':
			return 'mt';
		case 'Marshall Islands':
			return 'mh';
		case 'Martinique':
			return 'mq';
		case 'Mauritania':
			return 'mr';
		case 'Mauritius':
			return 'mu';
		case 'Mayotte':
			return 'yt';
		case 'Mexico':
			return 'mx';
		case 'Moldova':
			return 'md';
		case 'Monaco':
			return 'mc';
		case 'Mongolia':
			return 'mn';
		case 'Montenegro':
			return 'me';
		case 'Montserrat':
			return 'ms';
		case 'Morocco':
			return 'ma';
		case 'Mozambique':
			return 'mz';
		case 'Namibia':
			return 'na';
		case 'Nauru':
			return 'nr';
		case 'Nepal':
			return 'np';
		case 'Netherlands':
			return 'nl';
		case 'New Caledonia':
			return 'nc';
		case 'New Zealand':
			return 'nz';
		case 'Nicaragua':
			return 'ni';
		case 'Niger':
			return 'ne';
		case 'Nigeria':
			return 'ng';
		case 'Niue':
			return 'nu';
		case 'Norfolk Island':
			return 'nf';
		case 'Northern Mariana Islands':
			return 'mp';
		case 'Norway':
			return 'no';
		case 'Oman':
			return 'om';
		case 'Pakistan':
			return 'pk';
		case 'Palau':
			return 'pw';
		case 'Panama':
			return 'pa';
		case 'Papua New Guinea':
			return 'pg';
		case 'Paraguay':
			return 'py';
		case 'Peru':
			return 'pe';
		case 'Philippines':
			return 'ph';
		case 'Poland':
			return 'pl';
		case 'Portugal':
			return 'pt';
		case 'Puerto Rico':
			return 'pr';
		case 'Qatar':
			return 'qa';
		case 'Republic of the Congo':
			return 'cg';
		case 'Romania':
			return 'ro';
		case 'Rwanda':
			return 'rw';
		case 'Saint Kitts and Nevis':
			return 'kn';
		case 'Saint Lucia':
			return 'lc';
		case 'Saint Pierre and Miquelon':
			return 'pm';
		case 'Saint Vincent and the Grenadines':
			return 'vc';
		case 'Samoa':
			return 'ws';
		case 'San Marino':
			return 'sm';
		case 'Sao Tome and Principe':
			return 'st';
		case 'Saudi Arabia':
			return 'sa';
		case 'Scotland':
			return 'gb';
		case 'Senegal':
			return 'sn';
		case 'Serbia':
			return 'rs';
		case 'Seychelles':
			return 'sc';
		case 'Sierra Leone':
			return 'sl';
		case 'Singapore':
			return 'sg';
		case 'Slovakia':
			return 'sk';
		case 'Slovenia':
			return 'si';
		case 'Solomon Islands':
			return 'sb';
		case 'Somalia':
			return 'so';
		case 'South Africa':
			return 'za';
		case 'South Sudan':
			return 'ss';
		case 'Spain':
			return 'es';
		case 'Sri Lanka':
			return 'lk';
		case 'Sudan':
			return 'sd';
		case 'Suriname':
			return 'sr';
		case 'Sweden':
			return 'se';
		case 'Taiwan':
			return 'tw';
		case 'Tajikistan':
			return 'tj';
		case 'Tanzania':
			return 'tz';
		case 'Thailand':
			return 'th';
		case 'Togo':
			return 'tg';
		case 'Tokelau':
			return 'tk';
		case 'Tonga':
			return 'to';
		case 'Trinidad and Tobago':
			return 'tt';
		case 'Tunisia':
			return 'tn';
		case 'Turkey':
			return 'tr';
		case 'Turkmenistan':
			return 'tm';
		case 'Turks and Caicos Islands':
			return 'tc';
		case 'Tuvalu':
			return 'tv';
		case 'Uganda':
			return 'ug';
		case 'Ukraine':
			return 'ua';
		case 'United Arab Emirates':
			return 'ae';
		case 'United Kingdom':
			return 'gb';
		case 'United States':
			return 'us';
		case 'Uruguay':
			return 'uy';
		case 'Uzbekistan':
			return 'uz';
		case 'Vanuatu':
			return 'vu';
		case 'Venezuela':
			return 've';
		case 'Vietnam':
			return 'vn';
		case 'Wallis and Futuna':
			return 'wf';
		case 'Western Sahara':
			return 'eh';
		case 'World':
			return 'xx';
		case 'Yemen':
			return 'ye';
		case 'Zambia':
			return 'zm';
		case 'Zimbabwe':
			return 'zw';
		default:
			return;
	}
}

export function sanitizeText(text) {
	return sanitizeHtml(text, {
		allowedTags: ['u', 'br', 'b', 'i', 'em', 'strong'],
		allowedAttributes: {},
	});
}

export function getCookie(name: string): string | null {
	if (typeof window === 'undefined') return null;

	const match = document?.cookie.match(
		new RegExp('(^|; )' + name + '=([^;]*)')
	);
	return match ? decodeURIComponent(match[2]) : null;
}
